import { ANALYTICS_HEAP_IDS, formatHeapId } from '@/common/constants';
import { CTA } from '@/components/CTA';
import { ActionCloseIcon, MenuIcon } from '@eventbrite/marmalade/icons';
import classNames from 'classnames';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { EBLogoMobile } from '../Icons/EBLogo';
import { Expansion } from '../blocks/mobile';
import { NavbarProps } from '../types';
import styles from './MobileNavbar.module.scss';

export const MobileNavbar: React.FC<NavbarProps> = ({
  title,
  titleLink,
  navItems,
  ctas,
  className,
  isT3Treatment = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  const handleCloseExpansion = () => {
    setIsExpanded(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const documentHeight = document.body.scrollHeight;
      const currentScroll = window.scrollY + window.innerHeight;
      const modifier = 10;
      const hasReachedBottom = currentScroll + modifier > documentHeight;

      // Check if we've scrolled past half of the first section
      const mainElement = document.querySelector('main');
      let hasScrolledPastFirstSection = false;

      if (mainElement) {
        const sections = mainElement.querySelectorAll('section');
        if (sections.length >= 1) {
          const firstSection = sections[0];
          const firstSectionBottom = (firstSection.offsetTop + firstSection.offsetHeight) / 2;
          const viewportTop = window.scrollY;

          // We've scrolled past the half of the first section if viewport top is below first section bottom
          hasScrolledPastFirstSection = viewportTop > firstSectionBottom;
        }
      }

      setShowStickyCTA(hasScrolledPastFirstSection && !hasReachedBottom);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle resize and scroll lock based on isExpanded state
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      if (window.innerWidth >= 1201 && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('resize', handleResize);

    if (isExpanded) {
      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.touchAction = 'none'; // iOS fix
    } else {
      // Unlock scroll
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    }

    // Clean up on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    };
  }, [isExpanded]);

  useEffect(() => {
    const handleRouteChange = () => setIsExpanded(false);
    Router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  // Focus trap for mobile navbar expansion
  useEffect(() => {
    if (!isExpanded || typeof window === 'undefined') return;

    const expansionElement = document.querySelector(
      '[aria-label="Mobile Main navigation"]',
    ) as HTMLElement;
    const ctaElement = document.querySelector('[class*="expansion_ctas"]') as HTMLElement;
    const closeButton = document.querySelector('[aria-label="Close Navbar Menu"]') as HTMLElement;

    if (!expansionElement) return;

    const focusableElements = expansionElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    const ctaFocusableElements = ctaElement?.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])',
    );

    const allFocusableElements = [
      closeButton,
      ...Array.from(focusableElements),
      ...Array.from(ctaFocusableElements || []),
    ].filter(Boolean) as HTMLElement[];

    const firstFocusableElement = allFocusableElements[0];
    const lastFocusableElement = allFocusableElements[allFocusableElements.length - 1];

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement?.focus();
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement?.focus();
        }
      }
    };

    // Focus the first element when expansion opens
    if (firstFocusableElement) {
      firstFocusableElement.focus();
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  return (
    <>
      <div
        className={classNames(
          className,
          isT3Treatment && isExpanded && styles.treatment_wrapper_expanded,
        )}
        onClick={(event) => {
          if (isT3Treatment && event.target === event.currentTarget) {
            handleCloseExpansion();
          }
        }}
      >
        <header
          role="banner"
          aria-label="Mobile Site Header"
          className={classNames(
            styles.navbar_container,
            isT3Treatment && styles.treatment_navbar_container,
            isT3Treatment && isExpanded && styles.treatment_navbar_expanded,
          )}
        >
          <div className={styles.navbar_brand}>
            <a
              href="/"
              aria-label="Eventbrite Homepage"
              data-heap-id={formatHeapId([
                ANALYTICS_HEAP_IDS.MOBILE,
                ANALYTICS_HEAP_IDS.NAVBAR,
                'brand-logo',
              ])}
            >
              <EBLogoMobile />
            </a>
            <a
              href={titleLink}
              className={styles.title}
              aria-label="MLP Homepage"
              data-heap-id={formatHeapId([
                ANALYTICS_HEAP_IDS.MOBILE,
                ANALYTICS_HEAP_IDS.NAVBAR,
                'mlp-logo',
              ])}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={title} alt="MLP Logo" loading="lazy" width="100%" height="20px" />
            </a>
          </div>
          <div
            className={classNames(
              styles.trailing_items,
              isT3Treatment && styles.treatment_trailing_items,
            )}
          >
            {isExpanded ? (
              <button
                aria-label="Close Navbar Menu"
                aria-controls="main navigation close"
                className={styles.trailing_items__close}
                onClick={() => {
                  handleCloseExpansion();
                }}
              >
                {isT3Treatment && (
                  <span className={styles.menu_label}>
                    <FormattedMessage
                      id="Menu"
                      defaultMessage="Menu"
                      description="Label for the mobile navigation control"
                    />
                  </span>
                )}
                <ActionCloseIcon size="MD" color="var(--marm-color-action-fg-base)" />
              </button>
            ) : (
              <button
                aria-label="Open Navbar Menu"
                aria-controls="main navigation open"
                aria-haspopup="true"
                aria-expanded={isExpanded}
                className={styles.trailing_items__hamburger}
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                {isT3Treatment && (
                  <span className={styles.menu_label}>
                    <FormattedMessage
                      id="Menu"
                      defaultMessage="Menu"
                      description="Label for the mobile navigation control"
                    />
                  </span>
                )}
                <MenuIcon size="MD" color="var(--marm-color-action-fg-base)" />
              </button>
            )}
            {/* <Avatar
              icon={<UserAvatar color="var(--marm-color-fg-tertiary)" />}
              size="medium"
              placeholder="User"
              className={styles.trailing_items__user_avatar}
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
            /> */}
          </div>
        </header>
        {isExpanded && (
          <Expansion
            items={navItems}
            ctas={ctas}
            isT3Treatment={isT3Treatment}
            onNavigate={handleCloseExpansion}
          />
        )}
      </div>
      {ctas && (
        <div className={`${styles.sticky_ctas} ${showStickyCTA ? styles.visible : ''}`}>
          {ctas.map((cta, index) => (
            <CTA {...cta} key={cta.label + index} dataHeapId={`${cta.dataHeapId}-sticky`} />
          ))}
        </div>
      )}
    </>
  );
};
